const DEFAULT_ROTATION_THRESHOLD_DEGREES = 8;

function finiteVector(value, fallback) {
  if (!Array.isArray(value) || value.length < 3) return fallback;
  const vector = value.slice(0, 3).map(Number);
  return vector.every(Number.isFinite) ? vector : fallback;
}
function normalizeDegrees(value) {
  let normalized = Number(value) || 0;
  while (normalized > 180) normalized -= 360;
  while (normalized <= -180) normalized += 360;
  return normalized;
}

function horizontalSector(yawDegrees) {
  const sectors = [
    "front",
    "front-right",
    "right",
    "back-right",
    "behind",
    "back-left",
    "left",
    "front-left",
  ];
  const index = Math.round(normalizeDegrees(yawDegrees) / 45);
  return sectors[(index + 8) % 8];
}

export function describeCameraView(
  direction,
  previousDirection = null,
  rotationThresholdDegrees = DEFAULT_ROTATION_THRESHOLD_DEGREES
) {
  const current = finiteVector(direction, [0, 0, -1]);
  const length = Math.hypot(...current) || 1;
  const normalized = current.map((value) => value / length);
  const yawDegrees = normalizeDegrees(
    (Math.atan2(normalized[0], -normalized[2]) * 180) / Math.PI
  );
  const pitchDegrees = (Math.asin(Math.max(-1, Math.min(1, normalized[1]))) * 180) / Math.PI;
  const previous = previousDirection ? describeCameraView(previousDirection) : null;
  const deltaDegrees = previous ? normalizeDegrees(yawDegrees - previous.yaw_degrees) : 0;
  const rotationDirection = Math.abs(deltaDegrees) < rotationThresholdDegrees
    ? "stable"
    : deltaDegrees > 0
      ? "right"
      : "left";

  return {
    yaw_degrees: Number(yawDegrees.toFixed(1)),
    pitch_degrees: Number(pitchDegrees.toFixed(1)),
    horizontal_sector: horizontalSector(yawDegrees),
    vertical_band: pitchDegrees > 15 ? "up" : pitchDegrees < -15 ? "down" : "level",
    previous_horizontal_sector: previous?.horizontal_sector || null,
    rotation_direction: rotationDirection,
    rotation_delta_degrees: Number(deltaDegrees.toFixed(1)),
  };
}

export function describeObjectSpatialRelation({
  cameraPosition,
  cameraDirection,
  objectPosition,
} = {}) {
  const camera = finiteVector(cameraPosition, [0, 0, 0]);
  const object = finiteVector(objectPosition, null);
  if (!object) return null;

  const rawForward = finiteVector(cameraDirection, [0, 0, -1]);
  const horizontalLength = Math.hypot(rawForward[0], rawForward[2]) || 1;
  const forward = [rawForward[0] / horizontalLength, 0, rawForward[2] / horizontalLength];
  const right = [-forward[2], 0, forward[0]];
  const offset = object.map((value, index) => value - camera[index]);
  const distance = Math.hypot(...offset);
  if (!distance) {
    return { relation: "here", bearing_degrees: 0, elevation_degrees: 0, distance: 0 };
  }

  const horizontalDistance = Math.hypot(offset[0], offset[2]) || 0.0001;
  const forwardDot = offset[0] * forward[0] + offset[2] * forward[2];
  const rightDot = offset[0] * right[0] + offset[2] * right[2];
  const bearingDegrees = normalizeDegrees((Math.atan2(rightDot, forwardDot) * 180) / Math.PI);
  const elevationDegrees = (Math.atan2(offset[1], horizontalDistance) * 180) / Math.PI;

  return {
    relation: horizontalSector(bearingDegrees),
    bearing_degrees: Number(bearingDegrees.toFixed(1)),
    elevation_degrees: Number(elevationDegrees.toFixed(1)),
    distance: Number(distance.toFixed(2)),
  };
}
